import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterBar, { type CategoryFilters } from "@/pages/category/components/FilterBar";
import { filterMedia, typeLabels, type MediaType } from "@/utils/media";

const PAGE_SIZE = 9;

const typeMeta: Record<MediaType, { title: string; desc: string; icon: string }> = {
  movie: {
    title: "电影分类",
    desc: "最新电影专区：汇聚亚洲各国口碑佳作，支持按地区、年份与热度筛选",
    icon: "ri-film-line",
  },
  drama: {
    title: "电视剧分类",
    desc: "热播剧集专区：日韩与国产剧集一网打尽，按地区、年份与评分自由筛选",
    icon: "ri-tv-2-line",
  },
  variety: {
    title: "综艺分类",
    desc: "综艺节目专区：美食、选秀与旅行真人秀，轻松一刻随时开看",
    icon: "ri-mic-2-line",
  },
};

function isMediaType(value: string | undefined): value is MediaType {
  return value === "movie" || value === "drama" || value === "variety";
}

export default function CategoryPage() {
  const { type } = useParams();
  const validType = isMediaType(type) ? type : null;
  const meta = validType ? typeMeta[validType] : null;

  const [filters, setFilters] = useState<CategoryFilters>({
    region: "all",
    year: "all",
    sort: "updated",
  });
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: meta
      ? `${meta.title} - 亚洲一区二区在线`
      : "分类不存在 - 亚洲一区二区在线",
    description: meta
      ? `${meta.desc}。亚洲一区二区在线提供高清亚洲影视在线浏览与筛选。`
      : "未找到对应的影视分类。",
    keywords: meta
      ? `${typeLabels[validType as MediaType]},亚洲电影,亚洲电视剧,亚洲综艺,亚洲一区二区在线`
      : "亚洲一区二区在线,分类不存在",
  });

  const list = useMemo(() => {
    if (!validType) return [];
    return filterMedia({ type: validType, ...filters });
  }, [validType, filters]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilters = (next: Partial<CategoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ region: "all", year: "all", sort: "updated" });
    setPage(1);
  };

  if (!validType || !meta) {
    return (
      <div className="mx-auto w-full px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的影视分类不存在或已调整，请返回首页选择电影、电视剧或综艺分类。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[{ label: "分类", to: "/category/movie" }, { label: typeLabels[validType] }]}
      />

      <div className="mt-4 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <i className={`${meta.icon} text-2xl`}></i>
        </span>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {meta.title}
          </h1>
          <p className="mt-0.5 text-sm text-foreground-500">{meta.desc}</p>
        </div>
      </div>

      <div className="mt-6">
        <FilterBar filters={filters} onChange={updateFilters} />
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-foreground-500">
        <span>
          共 <strong className="text-accent-600">{list.length}</strong> 部符合条件的作品
        </span>
        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center gap-1 text-foreground-600 transition-colors hover:text-primary-600"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>

      <div className="mt-5">
        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {paged.map((media) => (
                <MediaCard key={media.id} media={media} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无符合条件的影视内容"
            description="当前筛选条件下没有找到影视作品，试试放宽地区或年份条件，或直接重置筛选。"
            onAction={resetFilters}
            actionLabel="浏览全部电影"
            actionTo="/category/movie"
          />
        )}
      </div>
    </div>
  );
}